import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Group, Paper, Stack, Text, UnstyledButton } from '@mantine/core';
import { IconChevronRight } from '@tabler/icons-react';
import { useEssaysSummary } from '../../../hooks/useEssayApi';
import { useQuarterlySummary } from '../../../hooks/useQuarterlyApi';
import { useScoreResults } from '../../../hooks/useQuarterlyScoreApi';
import { useGetPerformanceReviewRoundsIdProgress } from '../../../api/generated/performance-review/performance-review';
import { monthLabel, reportingPeriod } from '../../../components/essays/essayPeriod';
import { reportingQuarter } from '../../../components/essays/quarterPeriod';
import { hasCompanyGrant, hasGrantOnOthers, useMyPermissions } from '../../../hooks/usePermission';
import { useCompanyModules } from '../../../hooks/useCompanyModules';

/** Ответ `/performance-review/rounds/:id/progress`: в сгенерированном клиенте он без схемы. */
interface RoundProgress {
  participants: number;
  selfForms: { total: number; submitted: number };
  peerForms: { total: number; submitted: number };
  summaries: { total: number; published: number };
}

/** Что показывать про раунд, зависит от его стадии: на каждой ждут своего. */
function roundLine(
  progress: RoundProgress,
  stage: string | undefined,
): { key: string; done: number; total: number } | null {
  if (stage === 'collect-feedback' && progress.peerForms.total > 0) {
    return {
      key: 'roundPeer',
      done: progress.peerForms.submitted,
      total: progress.peerForms.total,
    };
  }
  if (stage === 'summary' && progress.summaries.total > 0) {
    return {
      key: 'roundSummaries',
      done: progress.summaries.published,
      total: progress.summaries.total,
    };
  }
  if (progress.selfForms.total > 0) {
    return {
      key: 'roundSelf',
      done: progress.selfForms.submitted,
      total: progress.selfForms.total,
    };
  }
  return null;
}

/** Право, которым строка открывается; на всю компанию — строка про всех, а не про команду. */
const ROW_PERMISSION = {
  essays: 'essays.read',
  quarterly: 'quarterly.read',
  round: 'reviews.progress',
  results: 'quarterly.scores.results',
} as const;

interface Row {
  id: keyof typeof ROW_PERMISSION;
  label: string;
  value: string;
  to: string;
}

/**
 * Что происходит у других: сдача эссе и отчётов, ход раунда ревью, готовность
 * итогов квартала. Каждая строка приходит вместе с правом на неё — лид видит
 * свою команду, админ и держатель права «видит все оценки» всю организацию,
 * рядовой сотрудник не видит блока вовсе.
 */
export const CompanyBlock: React.FC<{ roundId?: string; roundStage?: string }> = ({
  roundId,
  roundStage,
}) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'ru';
  const { data: perms } = useMyPermissions();
  // Строки неподключённых компании разделов не запрашиваем и не показываем.
  const { hasModule } = useCompanyModules();
  const hasEssays = hasModule('essays');
  const hasQuarterly = hasModule('quarterly');

  const month = React.useMemo(() => reportingPeriod(), []);
  const quarter = React.useMemo(() => reportingQuarter(), []);

  const { data: essays } = useEssaysSummary(
    month.year,
    month.month,
    hasEssays && hasGrantOnOthers(perms, 'essays.read'),
  );
  const { data: reports } = useQuarterlySummary(
    quarter.year,
    quarter.quarter,
    hasQuarterly && hasGrantOnOthers(perms, 'quarterly.read'),
  );
  const { data: results } = useScoreResults(
    quarter.year,
    quarter.quarter,
    hasQuarterly && hasCompanyGrant(perms, 'quarterly.scores.results'),
  );
  const { data: roundData } = useGetPerformanceReviewRoundsIdProgress(roundId || '', {
    query: { enabled: !!roundId && hasCompanyGrant(perms, 'reviews.progress') },
  });
  const round = roundData as unknown as RoundProgress | undefined;

  const ofTotal = (done: number, total: number) => t('dashboard.company.ofTotal', { done, total });

  const rows: Row[] = [];

  if (hasEssays && essays?.length) {
    rows.push({
      id: 'essays',
      label: t('dashboard.company.essays', { month: monthLabel(month.year, month.month, lang) }),
      value: ofTotal(essays.filter((r) => r.status === 'submitted').length, essays.length),
      to: '/essays/manage',
    });
  }

  if (hasQuarterly && reports?.length) {
    rows.push({
      id: 'quarterly',
      label: t('dashboard.company.quarterly', { quarter: quarter.quarter }),
      value: ofTotal(reports.filter((r) => r.status === 'submitted').length, reports.length),
      to: '/essays/manage',
    });
  }

  const roundRow = round ? roundLine(round, roundStage) : null;
  if (roundRow) {
    rows.push({
      id: 'round',
      label: t(`dashboard.company.${roundRow.key}`),
      value: ofTotal(roundRow.done, roundRow.total),
      // Прогресс раунда видит и наблюдатель, но админка раунда — только с reviews.manage:
      // наблюдателя ведём на результаты, куда у него есть доступ.
      to: hasCompanyGrant(perms, 'reviews.manage')
        ? '/performance-review/admin'
        : `/performance-review/${roundId}/results`,
    });
  }

  // Итоги открываются только после закрытия окна: до этого хук отдаёт 409 и
  // строке неоткуда взяться.
  if (hasQuarterly && results?.rows.length) {
    rows.push({
      id: 'results',
      label: t('dashboard.company.results', { quarter: quarter.quarter }),
      value: ofTotal(results.rows.filter((r) => r.total !== null).length, results.rows.length),
      to: '/quarterly-scoring/results',
    });
  }

  if (rows.length === 0) return null;

  // Заголовок должен говорить правду о том, чьи это числа: «у всех» — если хоть одна строка
  // открыта правом на всю компанию (с сервера), иначе это команда или вертикаль.
  const seesEveryone = rows.some((row) => hasCompanyGrant(perms, ROW_PERMISSION[row.id]));

  return (
    <Stack gap="xs">
      <Text size="sm" fw={500}>
        {t(seesEveryone ? 'dashboard.company.titleAll' : 'dashboard.company.titleTeam')}
      </Text>
      <Paper withBorder radius="md" className="todo-list">
        <Stack gap={0}>
          {rows.map((row) => (
            <UnstyledButton
              key={row.id}
              className="todo-list__item"
              onClick={() => navigate(row.to)}
            >
              <Group gap="sm" wrap="nowrap" align="center">
                <Text size="sm" style={{ flex: 1, minWidth: 0 }}>
                  {row.label}
                </Text>
                <Text size="sm" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {row.value}
                </Text>
                <IconChevronRight size={16} className="todo-list__chevron" />
              </Group>
            </UnstyledButton>
          ))}
        </Stack>
      </Paper>
    </Stack>
  );
};
